import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";
import { fmtNumber } from "@/lib/format";

const B = OPS.salesBudget;

export default function ImportTargetsDialog({ open, onOpenChange, onImported }) {
    const { t, lang } = useI18n();
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const fileRef = useRef(null);

    useEffect(() => {
        if (open) {
            setFile(null);
            setResult(null);
            setError(null);
        }
    }, [open]);

    const doUpload = async () => {
        if (!file) return;
        setUploading(true);
        setResult(null);
        setError(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/sales-budget/targets/import", fd, { timeout: 120000 });
            setResult(r.data);
            if (r.data.ok_count > 0) toast.success(t("import_done"));
            onImported && onImported();
        } catch (e) {
            const msg = e?.response?.data?.detail;
            setError(typeof msg === "string" ? msg : t("error_generic"));
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={B.importDialog} className="max-w-2xl max-h-[88vh] overflow-y-auto scrollbar-thin">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="h-5 w-5 text-[color:var(--brand-orange)]" />
                        {t("budget_import_title")}
                    </DialogTitle>
                    <DialogDescription>{t("budget_import_desc")}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* format hint */}
                    <div className="rounded-lg surface-2 border border-[color:var(--border-soft)] p-3 text-xs text-[color:var(--ink-muted)]">
                        <p className="font-semibold text-[color:var(--ink)] mb-1">{t("budget_csv_format")}</p>
                        <code dir="ltr" className="block font-mono text-[11px] text-[color:var(--brand-brown)]">
                            branch,month,target
                        </code>
                        <p className="mt-1">{t("budget_csv_hint")}</p>
                    </div>

                    {/* File picker */}
                    <div
                        className="rounded-lg border border-dashed border-[color:var(--border-strong)] surface-2 p-4 flex items-center gap-3 cursor-pointer"
                        onClick={() => fileRef.current?.click()}
                    >
                        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[color:rgba(186,217,235,0.5)]">
                            <FileSpreadsheet className="h-5 w-5 text-[color:var(--brand-brown)]" />
                        </span>
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-[color:var(--ink)]">
                                {file ? `${t("selected_file")}: ${file.name}` : t("budget_choose_csv")}
                            </p>
                            <p className="text-xs text-[color:var(--ink-muted)]" dir="ltr">
                                CSV · UTF-8
                            </p>
                        </div>
                        <input
                            ref={fileRef}
                            data-testid={B.importFile}
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            onChange={(e) => {
                                setFile(e.target.files?.[0] || null);
                                setResult(null);
                                setError(null);
                            }}
                        />
                    </div>

                    {error ? (
                        <div className="rounded-lg border border-[color:var(--danger)] bg-[color:rgba(220,38,38,0.06)] p-3 flex items-start gap-2 text-xs text-[color:var(--danger)]">
                            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                            <span className="leading-relaxed">{error}</span>
                        </div>
                    ) : null}

                    {result ? (
                        <div data-testid={B.importResult} className="rounded-lg surface-2 border border-[color:var(--border-soft)] p-3 space-y-3">
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                                <span className="flex items-center gap-1.5 font-semibold text-[color:var(--success)]">
                                    <CheckCircle2 className="h-4 w-4" />
                                    {t("budget_imported")}: <bdi dir="ltr" className="tnum">{result.imported}</bdi>
                                </span>
                                <span className="text-[color:var(--ink-muted)]">
                                    · {t("budget_updated")}: <bdi dir="ltr" className="tnum">{result.updated}</bdi>
                                </span>
                                <span className="text-[color:var(--ink-muted)]">
                                    · {t("budget_failed")}: <bdi dir="ltr" className={`tnum font-semibold ${result.failed_rows?.length ? "text-[color:var(--danger)]" : ""}`}>{result.failed_rows?.length || 0}</bdi>
                                </span>
                            </div>

                            {result.failed_rows?.length ? (
                                <div data-testid={B.importFailed} className="space-y-1.5">
                                    <p className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--brand-brown)]">
                                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                        {t("budget_failed_rows_title")}
                                    </p>
                                    <div className="max-h-[240px] overflow-y-auto scrollbar-thin rounded-lg border border-[color:var(--border-soft)]">
                                        <Table>
                                            <TableHeader className="sticky top-0 z-10">
                                                <TableRow className="bg-[color:rgba(224,222,217,0.92)] hover:bg-[color:rgba(224,222,217,0.92)]">
                                                    <TableHead className="text-start whitespace-nowrap">#</TableHead>
                                                    <TableHead className="text-start whitespace-nowrap">{t("budget_col_branch")}</TableHead>
                                                    <TableHead className="text-start whitespace-nowrap">{t("budget_col_month")}</TableHead>
                                                    <TableHead className="text-start">{t("budget_reason")}</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {result.failed_rows.map((f) => (
                                                    <TableRow key={`${f.row}-${f.branch}`}>
                                                        <TableCell className="tnum text-[color:var(--ink-muted)]" dir="ltr">{f.row}</TableCell>
                                                        <TableCell className="font-medium text-[color:var(--ink)]">{f.branch || "—"}</TableCell>
                                                        <TableCell dir="ltr">{f.month || "—"}</TableCell>
                                                        <TableCell className="text-xs text-[color:var(--danger)]">{f.reason}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-xs text-[color:var(--success)] flex items-center gap-1.5">
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                    {t("budget_all_rows_ok")}
                                </p>
                            )}
                        </div>
                    ) : null}
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
                        {result ? t("close") : t("cancel")}
                    </Button>
                    <Button
                        data-testid={B.importUpload}
                        onClick={doUpload}
                        disabled={!file || uploading}
                        className="bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                    >
                        {uploading ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Upload className="h-4 w-4 me-2" />}
                        {uploading ? t("uploading") : t("upload")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
